"use client";

import { getLangFromLocale } from "@/apiUtils/commonUtils";
import { PromotionTilesType } from "@/common/lib/types";
import { cn } from "@/lib/tailwind";
import { buildGradientOverlayCss } from "@/utils/gradientOverlay";
import { getCloudinaryURL } from "@/utils/cloudinary";
import Image from "@/components/ui/imageV2/ImageV2";
import { Fragment } from "react";
import { CLink } from "../common/customLink/CustomLink";
import NoImageSrc from "@/assets/images/no_image.svg";
import VideoCard from "../common/videoCard/VideoCard";
import CtaTileBlock from "./CtaTileBlock";
import Button from "@/components/ui/button/Button";
import ImageCard from "../common/imageCard/ImageCard";

interface IProps {
  promo: {
    data: PromotionTilesType;
    span: string;
    pos: number;
    isFullRow?: boolean;
    mobileHeight: number;
    desktopHeight: number;
    mobileWidth: number;
    desktopWidth: number;
    mobileAspectRatio: string;
    desktopAspectRatio: string;
  };
  locale: string;
}

const PromoData = ({ promo, locale }: IProps) => {
  const language = getLangFromLocale(locale);
  const tileVariant = promo?.data?.tileVariant ?? "media";

  switch (tileVariant) {
    case "cta":
      return (
        <div className={promo.span}>
          <CtaTileBlock
            tileName={promo.data.tileName}
            ctaTiles={promo.data.ctaTiles ?? []}
            backgroundStyle={promo.data.backgroundStyle}
            locale={locale}
            mobileHeight={248}
            desktopHeight={280}
          />
        </div>
      );
    default: {
      const data = promo.data;
      const hasOverlay = data.presentation === "overlay";
      const overlayBackground = hasOverlay ? buildGradientOverlayCss(data.overlay) : undefined;
      const tileSurfaceStyle = {
        ...(data?.color && { backgroundColor: data.color }),
        ...(hasOverlay && { background: overlayBackground }),
      };
      const description = data?.description;
      const hasContent = !!(data?.tileName || description || data?.linkTitle);
      const wholeTileLink = data?.link?.url && !data?.linkTitle;

      const action = hasContent
        ? {
            title: data?.tileName ?? undefined,
            description: description as React.ReactNode,
            cta:
              data?.linkTitle && data?.link?.url ? (
                <CLink href={data.link.url}>
                  <Button
                    className="flex h-[36px] min-w-[120px] items-center justify-center text-size-10 font-semibold md:h-[50px] md:min-w-[170px] md:text-sm"
                    variant="text"
                  >
                    {data.linkTitle}
                  </Button>
                </CLink>
              ) : undefined,
          }
        : undefined;

      const wrap = (children: React.ReactNode) =>
        wholeTileLink ? <CLink href={data!.link!.url}>{children}</CLink> : <>{children}</>;

      const imageAction = hasContent
        ? {
            title: data?.tileName ?? undefined,
            description: description as React.ReactNode,
            cta:
              data?.linkTitle && data?.link?.url ? (
                <CLink href={data.link.url}>
                  <Button
                    className="flex h-[36px] min-w-[120px] items-center justify-center text-size-10 font-semibold md:h-[50px] md:min-w-[170px] md:text-sm"
                    variant="text"
                  >
                    {data.linkTitle}
                  </Button>
                </CLink>
              ) : undefined,
          }
        : undefined;

      return (
        <Fragment>
          {promo?.data.media?.smallMediaUrl || promo?.data?.video?.mobileVideo ? (
            <div className={cn("md:hidden", promo.span, promo.isFullRow && "py-4 md:py-4")}>
              {wrap(
                promo?.data?.contentType === "video" ? (
                  <VideoCard
                    showControls={promo?.data?.video?.showControls}
                    containerProps={{
                      className: cn("h-full w-full", hasOverlay && "relative"),
                      style: tileSurfaceStyle,
                    }}
                    videoProps={{
                      src: language === "ar" ? promo?.data?.video?.mobileVideo.ar : promo?.data?.video?.mobileVideo.en,
                      className: " w-full object-cover",
                      playsInline: true,
                      muted: true,
                      loop: promo?.data?.video?.loop,
                      poster: language === "ar" ? promo?.data?.video?.mobileVideoThumbnail?.ar : promo?.data?.video?.mobileVideoThumbnail?.en,
                      style: { aspectRatio: promo.mobileAspectRatio },
                    }}
                    action={action}
                  />
                ) : (
                  <ImageCard
                    containerProps={{
                      className: cn("h-full w-full", hasOverlay && "relative"),
                      style: tileSurfaceStyle,
                    }}
                    imageProps={{
                      src: promo?.data?.media ? getCloudinaryURL(promo?.data?.media?.smallMediaUrl?.[getLangFromLocale(locale)]) : NoImageSrc,
                      alt: data?.tileName ?? "promo-banner",
                      height: promo.mobileHeight,
                      width: promo.mobileWidth,
                      className: "w-full object-cover",
                      style: { aspectRatio: promo.mobileAspectRatio },
                    }}
                    action={imageAction ?? { title: undefined, description: undefined, cta: undefined }}
                  />
                )
              )}
            </div>
          ) : (
            <></>
          )}
          {promo?.data.media?.largeMediaUrl || promo?.data?.video?.desktopVideo ? (
            <div className={cn("hidden md:block", promo?.span, promo.isFullRow && "py-4 md:py-4")}>
              {wrap(
                promo?.data?.contentType === "video" ? (
                  <VideoCard
                    showControls={promo?.data?.video?.showControls}
                    containerProps={{
                      className: cn("h-full w-full", hasOverlay && "relative"),
                      style: tileSurfaceStyle,
                    }}
                    videoProps={{
                      src: language === "ar" ? promo?.data?.video?.desktopVideo.ar : promo?.data?.video?.desktopVideo.en,
                      className: "w-full object-cover",
                      playsInline: true,
                      muted: true,
                      loop: promo?.data?.video?.loop,
                      poster: language === "ar" ? promo?.data?.video?.desktopVideoThumbnail?.ar : promo?.data?.video?.desktopVideoThumbnail?.en,
                      style: { aspectRatio: promo.desktopAspectRatio },
                    }}
                    action={action}
                  />
                ) : (
                  <ImageCard
                    containerProps={{
                      className: cn("h-full w-full", hasOverlay && "relative"),
                      style: tileSurfaceStyle,
                    }}
                    imageProps={{
                      src: promo?.data?.media ? getCloudinaryURL(promo?.data?.media?.largeMediaUrl?.[getLangFromLocale(locale)]) : NoImageSrc,
                      alt: data?.tileName ?? "promo-banner",
                      height: promo.desktopHeight,
                      width: promo.desktopWidth,
                      className: "w-full object-cover",
                      style: { aspectRatio: promo.desktopAspectRatio },
                    }}
                    action={imageAction ?? { title: undefined, description: undefined, cta: undefined }}
                  />
                )
              )}
            </div>
          ) : (
            <></>
          )}
        </Fragment>
      );
    }
  }
};

export default PromoData;
